import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo } from '../mock';

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    country: '',
    serviceInterest: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const serviceOptions = [
    'AI-Powered Hospital Management Software',
    'Equipment Procurement',
    'Doctor & Partner Network',
    'Interior Design Guidelines',
    'Medicine & Herbal Supply',
    'Patient Engagement Platform',
    'Education & Training Programs',
    'Complete Turnkey Solution',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission (frontend-only with mock data)
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setSubmitStatus('success');
      setIsSubmitting(false);
      // Reset form
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        country: '',
        serviceInterest: '',
        message: '',
      });

      // Clear success message after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000);
    }, 1500);
  };

  return (
    <section
      id="contact"
      style={{
        background: 'var(--gradient-hero-overlay)',
        padding: '5rem 1.2rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ayurvedic Spices Background */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1506368249639-73a05d6f6488?q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.25,
          zIndex: 0,
        }}
      />
      
      {/* Warm Amber Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(217, 119, 6, 0.12) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />
      
      {/* Overlay gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--gradient-hero-overlay)',
          zIndex: 1,
        }}
      />
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              {t('contact.title')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t('contact.subtitle')}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '3rem',
            }}
          >
            {/* Contact Information */}
            <div>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Contact Information
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                    }}
                  >
                    <Mail size={20} color="var(--text-primary)" />
                  </div>
                  <div>
                    <div className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      Email
                    </div>
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {companyInfo.email}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                    }}
                  >
                    <Phone size={20} color="var(--text-primary)" />
                  </div>
                  <div>
                    <div className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      Phone
                    </div>
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {companyInfo.phone}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                    }}
                  >
                    <MapPin size={20} color="var(--text-primary)" />
                  </div>
                  <div>
                    <div className="body-medium" style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                      Address
                    </div>
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {companyInfo.address}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                borderRadius: '0.75rem',
                padding: '2rem',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {/* Name */}
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                      {t('contact.form.fullName')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                    />
                  </div>

                  {/* Organization */}
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                      {t('contact.form.organization')}
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                    />
                  </div>

                  {/* Phone & Country */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid var(--border-input)',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          background: 'white',
                          transition: 'border-color 0.2s ease',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                      />
                    </div>
                    <div>
                      <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                        {t('contact.form.country')}
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid var(--border-input)',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          background: 'white',
                          transition: 'border-color 0.2s ease',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                      />
                    </div>
                  </div>

                  {/* Service Interest */}
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                      {t('contact.form.serviceInterest')} *
                    </label>
                    <select
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        transition: 'border-color 0.2s ease',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-input)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        transition: 'border-color 0.2s ease',
                        resize: 'vertical',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--border-input-focus)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border-input)')}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary hover-scale"
                    style={{
                      width: '100%',
                      gap: '0.5rem',
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Message <Send size={16} />
                      </>
                    )}
                  </button>

                  {/* Success Message */}
                  {submitStatus === 'success' && (
                    <div
                      style={{
                        padding: '1rem',
                        background: 'var(--accent-green-200)',
                        borderRadius: '0.5rem',
                        textAlign: 'center',
                      }}
                    >
                      <span className="body-small" style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                        Thank you! We'll get back to you soon.
                      </span>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
